import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'
import { Block, ExternalLink, Heading, Paragraph } from 'suomifi-ui-components'
import { format } from 'date-fns'
import _ from 'lodash'
import { getBirthdayFromSsn } from 'common/src/vtj/ssn'
import { MaritalStatus } from 'todistukset-common/src/vtj/MaritalStatus.enum'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'
import { selectBirthPlaceByLang } from 'todistukset-common/src/vtj/birth-place.util'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { useUaetForm } from 'todistukset-ui/src/vtj/stores/uaet-form.store'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import {
  FormContainer,
  InfoInlineAlert,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { Columns } from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetCommonFormComponents.sc'
import InfoItem from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/InfoItem.react'
import {
  getDisplayValueForCountryCode,
  getDisplayValueForKansalaisuus,
} from 'todistukset-common/src/vtj/localization.util'
import { reversedDisplayNameOf } from 'todistukset-common/src/vtj/name.util'
import { MaritalStatusSelect } from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/MaritalStatusSelect.react'
import { useUaetFormActions } from 'todistukset-ui/src/vtj/stores/actions/uaet-form-actions'
import { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import { UaetUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { validateMaritalStatus } from 'todistukset-common/src/vtj/validation'
import {
  getErrorStatus,
  useFirstErrorImperativeHandle,
} from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import OrderFlowName from 'todistukset-ui/src/vtj/OrderFlowName.enum'

const UaetOrdererInfoStep: StepComponent = observer(
  forwardRef(function UaetOrdererInfoStep({ titleId }, ref) {
    const { t } = useTranslation()
    const {
      common: { lang, loggedInUser },
      steps: {
        [OrderFlowName.UAETTODISTUS]: { showStepValidationErrors: showErrors },
      },
    } = useStore()

    const user = loggedInUser.get()

    const form = useUaetForm()
    const {
      spouseA: {
        hetu,
        firstNames,
        lastName,
        maritalStatus,
        citizenshipCode,
        birthCountryCode,
        birthPlaceVTJ,
      },
    } = form

    const { setSpouseAMaritalStatus } = useUaetFormActions()

    const maritalStatusValue = finalEditableFieldValue(maritalStatus)

    const hasMaritalStatusVTJ = ![null, MaritalStatus.EI_TIETOA].includes(
      maritalStatus?.vtjArvo ?? null
    )

    if (!hetu) {
      // This is for satisfying typescript, it should be checked long before we get here.
      throw new Error('UAET user needs hetu')
    }

    const birthdate = format(getBirthdayFromSsn(hetu), 'dd.MM.yyyy')

    const displayedCitizenshipInfo = !_.isEmpty(
      finalEditableFieldValue(citizenshipCode)
    )
      ? getDisplayValueForKansalaisuus(citizenshipCode, lang)
      : t('aetOwnInfoNoInformation')

    const displayedBirthCountry = finalEditableFieldValue(birthCountryCode)
      ? getDisplayValueForCountryCode(
          finalEditableFieldValue(birthCountryCode),
          lang
        )
      : t('aetOwnInfoNoInformation')

    const birthPlaceValue = birthPlaceVTJ
      ? selectBirthPlaceByLang(birthPlaceVTJ, lang)
      : null
    const displayedBirthPlace = birthPlaceValue ?? t('aetOwnInfoNoInformation')

    const refs = useFirstErrorImperativeHandle(
      ref,
      form,
      getMaritalStatusErrors,
      ['maritalStatus']
    )

    const errorStatus = getErrorStatus({
      field: 'maritalStatus',
      showErrors,
      t,
      errors: {
        ...getMaritalStatusErrors(form, user),
      },
    })

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="aetOwnInfoMainTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetOwnInfoMainDescription_paragraphs" />
          <Paragraph>
            <ExternalLink
              href={t('aetOwnInfoUpdateVtjLinkHref')}
              labelNewWindow={t('opensInNewWindow')}
              underline="initial"
            >
              <LocalizedString stringKey="aetOwnInfoUpdateVtjLinkText" />
            </ExternalLink>
          </Paragraph>
        </Block>
        <Block variant="section">
          <Heading variant="h3">
            <LocalizedString stringKey="aetOwnInfoItemsTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetOwnInfoItemsDescription_paragraphs" />
          <Columns data-test-id="info-items">
            <InfoItem
              title={t('aetOwnInfoNameLabel')}
              content={`${reversedDisplayNameOf({ lastName, firstNames })}`}
            />
            <InfoItem
              title={t(
                'orderWizardPersonalInformationStepInputLabel_birthDate'
              )}
              content={birthdate}
            />
            <InfoItem
              title={t('aetOwnInfoCitizenshipLabel')}
              content={displayedCitizenshipInfo}
            />
            <InfoItem
              title={t(
                'orderWizardPersonalInformationStepInputLabel_birthPlace'
              )}
              content={displayedBirthPlace || ''}
            />
            <InfoItem
              title={t('birthCountry')}
              content={displayedBirthCountry}
            />
            {hasMaritalStatusVTJ && (
              <InfoItem
                title={t('aetOwnInfoMaritalStatusLabel')}
                content={t(`maritalStatus_${maritalStatusValue}`)}
              />
            )}
          </Columns>
        </Block>
        {!hasMaritalStatusVTJ && (
          <Block variant="section">
            <Heading variant="h3">
              <LocalizedString stringKey="aetOwnInfoMaritalStatusTitle" />
            </Heading>
            <LocalizedParagraphs stringKey="aetOwnInfoMaritalStatusDescription_paragraphs" />
            <Columns>
              <MaritalStatusSelect
                maritalStatus={maritalStatus}
                setMaritalStatus={setSpouseAMaritalStatus}
                maritalStatusRef={refs.maritalStatus}
                errorStatus={errorStatus}
              />
            </Columns>
            <InfoInlineAlert status="warning" ariaLiveMode="off" mb="l">
              <LocalizedParagraphs stringKey="uaetOwnInfoSelfProvidedMarriageStatus_paragraphs" />
            </InfoInlineAlert>
          </Block>
        )}
      </FormContainer>
    )
  })
)

function getMaritalStatusErrors(form: UaetUiForm, user: TodistuksetUser) {
  return {
    maritalStatus: validateMaritalStatus(
      finalEditableFieldValue(form.spouseA.maritalStatus),
      form,
      user
    ),
  }
}

export default UaetOrdererInfoStep
